import styled from '@emotion/styled';
import React, { forwardRef, useEffect, useRef } from 'react';

import { getColorValue, getFocusRing, u } from '../../utilities';

const SvgCheckboxCheck = props => (
  <svg width={12} height={9} viewBox="0 0 12 9" fill="none" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.707.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L4 6.586 10.293.293a1 1 0 011.414 0z"
      fill="currentColor"
    />
  </svg>
);

const SvgCheckboxMinus = props => (
  <svg width={12} height={2} viewBox="0 0 12 2" fill="none" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 1a1 1 0 011-1h10a1 1 0 110 2H1a1 1 0 01-1-1z"
      fill="currentColor"
    />
  </svg>
);

const CheckboxWrapper = styled.div`
  display: block;
  position: relative;
`;

const CheckboxInput = styled.input`
  height: 100%;
  margin: 0;
  opacity: 0;
  position: absolute;
  width: 100%;

  &:not(:disabled) {
    cursor: pointer;

    & ~ .CheckboxBox {
      border: 2px solid ${getColorValue('body')};
    }

    &:checked ~ .CheckboxBox,
    &:indeterminate ~ .CheckboxBox {
      background-color: ${getColorValue('body')};
    }
  }

  &:disabled,
  &:disabled ~ .CheckboxBox {
    pointer-events: none;
  }

  &:disabled ~ .CheckboxBox {
    background-color: ${getColorValue('disabledBackground')};

    .Icon {
      color: ${getColorValue('disabledContent')};
    }
  }

  &:focus-visible ~ .CheckboxBox {
    ${getFocusRing()};
  }

  & ~ .CheckboxBox .Icon {
    display: none;
    color: ${getColorValue('background')};
  }

  &:checked ~ .CheckboxBox .IconCheck {
    display: block;
  }

  &:indeterminate ~ .CheckboxBox .IconMinus {
    display: block;
  }
`;

const CheckboxBox = styled.span`
  cursor: pointer;
  width: ${u(2.25)};
  height: ${u(2.25)};
  border-radius: ${u(0.5)};
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

const Checkbox = forwardRef(
  (
    {
      isDisabled,
      isChecked,
      isIndeterminate,
      onFocus,
      onChange,
      onBlur,
      ...props
    }: CheckboxProps & React.ComponentProps<typeof CheckboxInput>,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const defaultRef = useRef<HTMLInputElement>();
    const checkboxRef =
      (ref as React.MutableRefObject<HTMLInputElement>) || defaultRef;

    const iconColor = getColorValue(
      isDisabled ? 'disabledContent' : 'currentColor'
    );

    useEffect(() => {
      checkboxRef.current.indeterminate = isIndeterminate;
    });

    return (
      <CheckboxWrapper>
        <CheckboxInput
          type="checkbox"
          disabled={isDisabled}
          checked={isChecked}
          onFocus={onFocus}
          onChange={onChange}
          onBlur={onBlur}
          ref={checkboxRef}
          aria-checked={isChecked}
          {...props}
        />
        <CheckboxBox className="CheckboxBox">
          <SvgCheckboxMinus className="Icon IconMinus" color={iconColor} />
          <SvgCheckboxCheck className="Icon IconCheck" color={iconColor} />
        </CheckboxBox>
      </CheckboxWrapper>
    );
  }
);

type CheckboxProps = {
  isChecked?: boolean;
  isIndeterminate?: boolean;
  isDisabled?: boolean;
  onFocus?: React.ReactEventHandler;
  onChange?: React.ReactEventHandler;
  onBlur?: React.ReactEventHandler;
};

export default Checkbox;
